import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Routes, Route, useLocation } from 'react-router-dom';
import { Store, Users, ShoppingBag, DollarSign, Plus, LogOut, Building2, ExternalLink, LayoutDashboard, Settings, Star, Trash2, AlertTriangle, Lock, Mail, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { platformApi } from '@/utils/platformApi';
import { getPasswordComplexityErrors, PASSWORD_HINT } from '@/utils/passwordValidation';
import PlatformSecuritySettings from './PlatformSecuritySettings';

export default function PlatformAdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await platformApi.get('/platform/auth/me');
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Please sign in to access the platform admin');
      navigate('/login');
    }
  };

  const handleLogout = async () => {
    try {
      await platformApi.post('/auth/logout');
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      navigate('/');
    }
  };

  const navItems = [
    { path: '/platform/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { path: '/platform/admin/stores', label: 'Stores', icon: Building2 },
    { path: '/platform/admin/users', label: 'All Users', icon: Users },
    { path: '/platform/admin/email-logs', label: 'Email Logs', icon: Mail },
    { path: '/platform/admin/security', label: 'Security', icon: Lock },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F5F3EF] to-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#4A6741] border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading platform dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Header */}
      <header className="border-b border-[#E8E4DC] bg-white sticky top-0 z-50">
        <div className="flex items-center justify-between px-6 py-4">
          <Link to="/platform/admin" className="flex items-center gap-3">
            <div className="h-10 w-10 bg-[#4A6741] rounded-lg flex items-center justify-center">
              <Store className="h-6 w-6 text-white" />
            </div>
            <div>
              <span className="text-xl font-bold text-[#2C3E2D]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Platform Admin
              </span>
              <p className="text-xs text-[#5A6B5C]">{user?.email}</p>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-[#5A6B5C] hover:text-[#4A6741] text-sm">
              View Landing Page
            </Link>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-[#5A6B5C] hover:text-[#4A6741]"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-[#E8E4DC] min-h-[calc(100vh-73px)] p-6">
          <nav className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.exact 
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-[#4A6741] text-white font-bold'
                      : 'text-[#5A6B5C] hover:text-[#2C3E2D] hover:bg-[#F5F3EF]'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="stores" element={<StoresPage />} />
            <Route path="users" element={<AllUsersPage />} />
            <Route path="email-logs" element={<EmailLogsPage />} />
            <Route path="security" element={<PlatformSecuritySettings />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

// Dashboard Overview Component
function DashboardOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await platformApi.get('/platform/admin/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading stats...</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-[#2C3E2D] mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
        Platform Overview
      </h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={<Building2 className="h-6 w-6" />}
          label="Total Stores"
          value={stats?.total_tenants || 0}
          subtext={`${stats?.active_tenants || 0} active`}
        />
        <StatCard
          icon={<Users className="h-6 w-6" />}
          label="Total Users"
          value={stats?.total_users || 0}
          subtext="Across all stores"
        />
        <StatCard
          icon={<ShoppingBag className="h-6 w-6" />}
          label="Total Orders"
          value={stats?.total_orders || 0}
          subtext="All time"
        />
        <StatCard
          icon={<DollarSign className="h-6 w-6" />}
          label="Total Revenue"
          value={`$${(stats?.total_revenue || 0).toLocaleString()}`}
          subtext="All stores combined"
        />
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link
          to="/platform/admin/stores"
          className="bg-white rounded-xl border border-[#E8E4DC] p-6 hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 bg-[#4A6741]/10 rounded-lg flex items-center justify-center text-[#4A6741]">
              <Building2 className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-bold text-[#2C3E2D]">Manage Stores</h3>
              <p className="text-sm text-[#5A6B5C]">Create, edit, and manage tenant stores</p>
            </div>
          </div>
        </Link>

        <Link
          to="/platform/admin/users"
          className="bg-white rounded-xl border border-[#E8E4DC] p-6 hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 bg-[#4A6741]/10 rounded-lg flex items-center justify-center text-[#4A6741]">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-bold text-[#2C3E2D]">View All Users</h3>
              <p className="text-sm text-[#5A6B5C]">See users across all stores</p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
}

// Stores Page Component
function StoresPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    admin_email: '',
    admin_password: '',
    admin_name: ''
  });

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      const response = await platformApi.get('/platform/admin/tenants');
      setTenants(response.data);
    } catch (error) {
      toast.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 50);
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.slug || !formData.admin_email || !formData.admin_password) {
      toast.error('Please fill in all required fields');
      return;
    }

    const pwErrors = getPasswordComplexityErrors(formData.admin_password);
    if (pwErrors.length > 0) {
      toast.error(`Password must contain ${pwErrors.join(', ')}.`);
      return;
    }

    try {
      setCreating(true);
      await platformApi.post('/platform/admin/tenants', {
        name: formData.name,
        slug: formData.slug.toLowerCase(),
        admin_email: formData.admin_email,
        admin_password: formData.admin_password,
        admin_name: formData.admin_name || null
      });
      
      toast.success(`Store "${formData.name}" created successfully!`);
      setShowCreateDialog(false);
      setFormData({ name: '', slug: '', admin_email: '', admin_password: '', admin_name: '' });
      loadTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create store');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (tenantId, newStatus) => {
    try {
      await platformApi.put(`/platform/admin/tenants/${tenantId}/status`, { status: newStatus });
      toast.success(`Store status updated to ${newStatus}`);
      loadTenants();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleShowcaseToggle = async (tenantId, isShowcase) => {
    try {
      await platformApi.put(`/platform/admin/tenants/${tenantId}/showcase`, { is_showcase: isShowcase });
      toast.success(isShowcase ? 'Store added to showcase' : 'Store removed from showcase');
      loadTenants();
    } catch (error) {
      toast.error('Failed to update showcase status');
    }
  };

  const handleDeleteClick = (tenant) => {
    setTenantToDelete(tenant);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteTenant = async () => {
    if (!tenantToDelete || deleteConfirmText !== tenantToDelete.slug) {
      toast.error('Please type the store URL to confirm deletion');
      return;
    }
    try {
      setDeleting(true);
      const response = await platformApi.delete(`/platform/admin/tenants/${tenantToDelete.tenant_id}`);
      toast.success(response.data.message || 'Store deleted successfully');
      setDeleteDialogOpen(false);
      setTenantToDelete(null);
      setDeleteConfirmText('');
      loadTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete store');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading stores...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#2C3E2D]" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Stores
        </h1>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-[#4A6741] hover:bg-[#3d5636]" data-testid="create-store-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Store
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Store</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTenant} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name">Store Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Bob's Golf Shop"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      name: e.target.value,
                      slug: prev.slug || generateSlug(e.target.value)
                    }));
                  }}
                  className="mt-1"
                  data-testid="create-store-name"
                />
              </div>
              <div>
                <Label htmlFor="slug">Store URL *</Label>
                <div className="flex items-center mt-1">
                  <span className="text-gray-500 text-sm bg-gray-100 px-3 py-2 rounded-l-md border border-r-0">/</span>
                  <Input
                    id="slug"
                    placeholder="your-store"
                    value={formData.slug}
                    onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value.toLowerCase() }))}
                    className="rounded-l-none"
                    data-testid="create-store-slug"
                  />
                </div>
              </div>
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Admin Account</p>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="admin_name">Admin Name</Label>
                    <Input
                      id="admin_name"
                      placeholder="e.g., John Smith"
                      value={formData.admin_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, admin_name: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin_email">Admin Email *</Label>
                    <Input
                      id="admin_email"
                      type="email"
                      placeholder="admin@example.com"
                      value={formData.admin_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, admin_email: e.target.value }))}
                      className="mt-1"
                      data-testid="create-store-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin_password">Admin Password *</Label>
                    <Input
                      id="admin_password"
                      type="password"
                      placeholder="Min 8 chars, upper/lower/number/symbol"
                      value={formData.admin_password}
                      onChange={(e) => setFormData(prev => ({ ...prev, admin_password: e.target.value }))}
                      className="mt-1"
                      data-testid="create-store-password"
                    />
                    <p className="text-xs text-gray-500 mt-1">{PASSWORD_HINT}</p>
                  </div>
                </div>
              </div>
              <Button
                type="submit"
                disabled={creating}
                className="w-full bg-[#4A6741] hover:bg-[#3d5636]"
                data-testid="create-store-submit"
              >
                {creating ? 'Creating...' : 'Create Store'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-xl border border-[#E8E4DC] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Store</th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">URL</th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Users</th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <Star className="h-3 w-3" />
                    Showcase
                  </div>
                </th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E4DC]">
              {tenants.map((tenant) => (
                <tr key={tenant.tenant_id} className="hover:bg-gray-50" data-testid={`tenant-row-${tenant.slug}`}>
                  <td className="px-6 py-4">
                    <div className="font-medium text-[#2C3E2D]">{tenant.name}</div>
                    <div className="text-xs text-gray-500">{tenant.tenant_id}</div>
                  </td>
                  <td className="px-6 py-4">
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded">/{tenant.slug}</code>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{tenant.user_count || 0}</td>
                  <td className="px-6 py-4">
                    <Select
                      value={tenant.status}
                      onValueChange={(value) => handleStatusChange(tenant.tenant_id, value)}
                    >
                      <SelectTrigger className="w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={tenant.is_showcase || false}
                        onCheckedChange={(checked) => handleShowcaseToggle(tenant.tenant_id, checked)}
                        data-testid={`showcase-toggle-${tenant.slug}`}
                      />
                      {tenant.is_showcase && (
                        <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <a
                        href={`/${tenant.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#4A6741] hover:text-[#3d5636] inline-flex items-center gap-1 text-sm"
                      >
                        View Store
                        <ExternalLink className="h-3 w-3" />
                      </a>
                      <button
                        onClick={() => handleDeleteClick(tenant)}
                        className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                        title="Delete store"
                        data-testid={`delete-store-${tenant.slug}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {tenants.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    No stores yet. Create your first store to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-700">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Delete Store
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              This action is permanent and cannot be undone. All data including products, orders, users, and settings will be deleted.
            </DialogDescription>
          </DialogHeader>
          
          {tenantToDelete && (
            <div className="space-y-4 py-2">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm font-bold text-red-800 mb-1">{tenantToDelete.name}</p>
                <p className="text-xs text-red-600">/{tenantToDelete.slug}</p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm text-gray-700">
                  Type <span className="font-mono font-bold text-red-700">{tenantToDelete.slug}</span> to confirm:
                </Label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={tenantToDelete.slug}
                  className="border-red-200 focus:border-red-400"
                  data-testid="platform-delete-confirm-input"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setDeleteDialogOpen(false); setTenantToDelete(null); setDeleteConfirmText(''); }}
              data-testid="platform-delete-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteTenant}
              disabled={deleting || deleteConfirmText !== tenantToDelete?.slug}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              data-testid="platform-delete-confirm-btn"
            >
              {deleting ? 'Deleting...' : 'Permanently Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// All Users Page Component
function AllUsersPage() {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load tenants first to map tenant_id to names
      const tenantsRes = await platformApi.get('/platform/admin/tenants');
      const tenantsMap = {};
      tenantsRes.data.forEach(t => {
        tenantsMap[t.tenant_id] = t;
      });
      setTenants(tenantsMap);

      // For now, we'll show users from all tenants
      // This would need a new backend endpoint to get all users
      // For now, showing a message
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load data');
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading users...</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-[#2C3E2D] mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
        All Users
      </h1>

      <div className="bg-white rounded-xl border border-[#E8E4DC] p-8 text-center">
        <Users className="h-12 w-12 text-[#5A6B5C] mx-auto mb-4" />
        <h3 className="text-lg font-bold text-[#2C3E2D] mb-2">User Management</h3>
        <p className="text-[#5A6B5C] mb-4">
          To manage users for a specific store, visit that store's admin panel.
        </p>
        <Link to="/platform/admin/stores">
          <Button variant="outline">
            Go to Stores
          </Button>
        </Link>
      </div>
    </div>
  );
}

// Email Logs Page Component
const EMAIL_STATUS_STYLES = {
  sent: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  skipped: 'bg-gray-100 text-gray-500',
};

const CATEGORY_LABELS = {
  order_confirmation: 'Order Confirmation',
  new_order_admin: 'New Order (Admin)',
  shipment_tracking: 'Shipment Tracking',
  trial_ending: 'Trial Ending',
  payment_failed: 'Payment Failed',
  fulfillment_payment_failed: 'Fulfillment Payment Failed',
  subscription_canceled: 'Subscription Canceled',
  renewal_success: 'Renewal Success',
  welcome: 'Welcome',
  mfa_otp: 'MFA Code',
  general: 'General',
};

function EmailLogsPage() {
  const [logs, setLogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [skip, setSkip] = useState(0);
  const [resendingLogId, setResendingLogId] = useState(null);
  const limit = 25;

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, categoryFilter, statusFilter]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const qs = new URLSearchParams();
      qs.set('skip', skip);
      qs.set('limit', limit);
      if (categoryFilter !== 'all') qs.set('category', categoryFilter);
      if (statusFilter !== 'all') qs.set('status', statusFilter);
      if (search) qs.set('search', search);
      const response = await platformApi.get(`/platform/admin/email-logs?${qs.toString()}`);
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
      setCategories(response.data.categories || []);
    } catch (error) {
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSkip(0);
    loadLogs();
  };

  const handleResend = async (logId) => {
    try {
      setResendingLogId(logId);
      const response = await platformApi.post(`/platform/admin/email-logs/${logId}/resend`, {});
      if (response.data.success) {
        toast.success('Email resent successfully');
      } else {
        toast.error('Resend attempt failed again - check the new log row for the error');
      }
      loadLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend email');
    } finally {
      setResendingLogId(null);
    }
  };

  return (
    <div data-testid="email-logs-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#2C3E2D]" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Email Logs
        </h1>
        <Button variant="outline" size="sm" onClick={loadLogs} data-testid="email-logs-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <form onSubmit={handleSearchSubmit} className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            placeholder="Search by recipient or subject..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="email-logs-search-input"
          />
        </div>
        <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setSkip(0); }}>
          <SelectTrigger className="w-[200px]" data-testid="email-logs-category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setSkip(0); }}>
          <SelectTrigger className="w-[160px]" data-testid="email-logs-status-filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="skipped">Skipped</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" className="bg-[#4A6741] hover:bg-[#3d5636]" data-testid="email-logs-search-btn">
          Search
        </Button>
      </form>

      <div className="bg-white rounded-xl border border-[#E8E4DC] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">To</th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Subject</th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Sent At</th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E4DC]">
              {loading ? (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-500">Loading...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-500">No emails match these filters.</td></tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.log_id} className="hover:bg-gray-50" data-testid={`email-log-row-${log.log_id}`}>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${EMAIL_STATUS_STYLES[log.status] || 'bg-gray-100 text-gray-500'}`}
                        title={log.error || ''}
                      >
                        {log.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{CATEGORY_LABELS[log.category] || log.category}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{log.to_email}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={log.subject}>{log.subject}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {log.created_at ? new Date(log.created_at).toLocaleString() : ''}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {log.status === 'failed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={resendingLogId === log.log_id}
                          onClick={() => handleResend(log.log_id)}
                          data-testid={`email-log-resend-btn-${log.log_id}`}
                        >
                          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${resendingLogId === log.log_id ? 'animate-spin' : ''}`} />
                          Resend
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t border-[#E8E4DC] text-sm text-gray-500">
          <span>{total} total emails</span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - limit))}
              data-testid="email-logs-prev-btn"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={skip + limit >= total}
              onClick={() => setSkip(skip + limit)}
              data-testid="email-logs-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, subtext }) {
  return (
    <div className="bg-white rounded-xl border border-[#E8E4DC] p-6">
      <div className="flex items-center gap-4">
        <div className="h-12 w-12 bg-[#4A6741]/10 rounded-lg flex items-center justify-center text-[#4A6741]">
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-[#2C3E2D]">{value}</p>
          <p className="text-sm text-[#5A6B5C]">{label}</p>
          {subtext && <p className="text-xs text-gray-400">{subtext}</p>}
        </div>
      </div>
    </div>
  );
}
